import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSessionClaims } from '@/hooks/use-session-claims';
import { useUpdateCryptoOnchainPlatformSettings } from '@/lib/api/crypto-onchain-platform';

import { CopyableValue } from './CopyableValue';
import { TESTNET_WALLET_KINDS, type PlatformSettings, type TestnetWalletKind } from './types';

type Props = {
  settings: PlatformSettings | null;
};

type Draft = Record<TestnetWalletKind, string>;

const draftOf = (settings: PlatformSettings | null): Draft => ({
  btc: settings?.testnetWallets.btc ?? '',
  ltc: settings?.testnetWallets.ltc ?? '',
});

/**
 * Where the platform share of a Bitcoin Signet or Litecoin Testnet payment goes (sandbox stores):
 * those testnets need their own wallets, written differently from mainnet ones. EVM, Solana and
 * Tron testnets use the platform wallets above. Superadmin-only, like the mainnet wallets; without a
 * wallet the share is waived on that testnet.
 */
export function PlatformTestnetWalletsPanel({ settings }: Props) {
  const { t } = useTranslation('manageCryptoPayments');
  const { isSuperAdmin } = useSessionClaims();
  const updateSettings = useUpdateCryptoOnchainPlatformSettings();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<Draft>(() => draftOf(settings));

  const saved = draftOf(settings);
  const changes = Object.fromEntries(
    TESTNET_WALLET_KINDS.map((kind) => [kind, draft[kind].trim()] as const).filter(
      ([kind, value]) => value !== saved[kind],
    ),
  );
  const hasChanges = Object.keys(changes).length > 0;
  const hasSpaces = TESTNET_WALLET_KINDS.some((kind) => /\s/.test(draft[kind].trim()));
  const canSave = hasChanges && !hasSpaces && !updateSettings.isPending;

  const handleOpenChange = (isOpen: boolean) => {
    setIsEditing(isOpen);
    if (isOpen) setDraft(draftOf(settings));
  };

  const handleSave = () => {
    if (!canSave) return;
    updateSettings.mutate({ testnetWallets: changes }, { onSuccess: () => setIsEditing(false) });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle>{t('testnetWallets.title')}</CardTitle>
            <CardDescription>{t('testnetWallets.description')}</CardDescription>
          </div>
          {isSuperAdmin && (
            <Dialog open={isEditing} onOpenChange={handleOpenChange}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={!settings?.configured}>
                  <Pencil className="h-3.5 w-3.5" />
                  {t('testnetWallets.edit')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{t('testnetWallets.editTitle')}</DialogTitle>
                  <DialogDescription>{t('wallets.editDescription')}</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4">
                  {TESTNET_WALLET_KINDS.map((kind) => (
                    <div key={kind} className="space-y-1.5">
                      <Label htmlFor={`platform-testnet-wallet-${kind}`}>{t(`testnetWallets.chains.${kind}`)}</Label>
                      <Input
                        id={`platform-testnet-wallet-${kind}`}
                        value={draft[kind]}
                        onChange={(event) => setDraft((current) => ({ ...current, [kind]: event.target.value }))}
                        className="font-mono text-sm"
                        autoComplete="off"
                        spellCheck={false}
                        disabled={updateSettings.isPending}
                      />
                      <p className="text-muted-foreground text-xs">{t(`testnetWallets.hints.${kind}`)}</p>
                    </div>
                  ))}
                </div>
                <DialogFooter className="items-center">
                  {hasSpaces && (
                    <span className="text-destructive mr-auto text-xs">{t('testnetWallets.noSpaces')}</span>
                  )}
                  <Button variant="ghost" onClick={() => setIsEditing(false)}>
                    {t('actions.cancel')}
                  </Button>
                  <Button disabled={!canSave} onClick={handleSave}>
                    {updateSettings.isPending ? t('actions.saving') : t('wallets.save')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-4 sm:grid-cols-2">
          {TESTNET_WALLET_KINDS.map((kind) => {
            const wallet = settings?.testnetWallets[kind] ?? null;
            const chainLabel = t(`testnetWallets.chains.${kind}`);
            return (
              <div key={kind} className="space-y-1">
                <dt className="text-muted-foreground text-xs font-medium uppercase tracking-wide">{chainLabel}</dt>
                <dd className="text-sm">
                  {wallet ? (
                    <CopyableValue value={wallet} label={chainLabel} />
                  ) : (
                    <span className="text-muted-foreground">{t('wallets.notSet')}</span>
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      </CardContent>
    </Card>
  );
}
